import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Beaker, FastForward, Rewind, RotateCw, Loader2 } from "lucide-react";

const SHIFT_PRESETS = [-7, -3, -1, 1, 3, 7];

/**
 * Admin-only card on the System Admin dashboard. Wipes/refreshes the Princeton
 * demo tenant or time-shifts all of its dated records to keep demos feeling fresh.
 * Backed by /api/admin/demo/princeton/{refresh, time-travel}.
 */
export default function PrincetonDemoCard() {
  const [busy, setBusy] = useState(null);

  const refresh = async () => {
    if (!window.confirm("Wipe and re-seed the Princeton demo tenant from scratch?")) return;
    setBusy("refresh");
    try { await api.post("/admin/demo/princeton/refresh"); toast.success("Princeton tenant reseeded"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); }
  };

  const shift = async (days) => {
    setBusy(`shift_${days}`);
    try {
      const { data } = await api.post(`/admin/demo/princeton/time-travel?days=${days}`);
      toast.success(`Shifted ${data.shifted_records ?? 0} records ${days >= 0 ? "+" : ""}${days}d`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); }
  };

  return (
    <section className="rounded-xl ink-card p-6" data-testid="princeton-demo-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-lg text-white flex items-center gap-2">
            <Beaker className="h-5 w-5" style={{ color: "var(--brand-secondary)" }} />
            Princeton Hill — Demo Tools
          </h2>
          <p className="text-sm text-white/55 mt-1 max-w-xl">
            Keep the Princeton showcase tenant feeling fresh. Time-travel shifts every dated record (daily logs, Apple Health, critical events, pitch data) forward or backward in lockstep.
          </p>
        </div>
        <Button onClick={refresh} disabled={busy === "refresh"} variant="outline" className="border-white/15 text-white bg-white/5 hover:bg-white/10" data-testid="princeton-refresh">
          {busy === "refresh" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCw className="h-4 w-4 mr-2" />}
          Full reset
        </Button>
      </div>

      <div className="mt-5">
        <div className="text-[10px] tracking-[0.3em] font-bold uppercase text-white/45 mb-3">Time-travel preset</div>
        <div className="flex flex-wrap gap-2">
          {SHIFT_PRESETS.map((d) => {
            const isForward = d > 0;
            const Icon = isForward ? FastForward : Rewind;
            const key = `shift_${d}`;
            return (
              <Button
                key={d}
                onClick={() => shift(d)}
                disabled={busy !== null}
                className="text-white font-bold rounded-full px-4"
                style={{ backgroundColor: isForward ? "var(--brand-secondary)" : "rgba(255,255,255,0.06)" }}
                data-testid={`princeton-shift-${d}`}
              >
                {busy === key ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <>
                    <Icon className="h-3.5 w-3.5 mr-1.5" />
                    {isForward ? "+" : ""}{d}d
                  </>
                )}
              </Button>
            );
          })}
        </div>
        <p className="text-[10px] text-white/40 mt-3">
          Tip: shift <span className="font-mono-num font-bold text-white/60">+7d</span> right before a sales call so Coleman&apos;s pain event landed &ldquo;yesterday&rdquo; in calendar time.
        </p>
      </div>
    </section>
  );
}
